import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * One export path for every report on the Leave Reports page.
 *
 * Both formats are driven from the same `columns` + `rows` pair, so CSV/Excel and PDF cannot
 * disagree about what a report contains — which is the requirement, and the thing two separate
 * generators would eventually break.
 *
 * Callers pass the array the table itself renders *after* filtering, never the page slice: the
 * page does not paginate its reports, but the rule matters regardless, because a caller handing in
 * `rows.slice(0, 50)` would silently produce a truncated export that looks complete.
 *
 * jsPDF and jspdf-autotable are already dependencies (LeaveDashboardPage uses them), so nothing new
 * is introduced here.
 */

export interface ExportColumn<T> {
  header: string;
  /** Cell text. Returning a number keeps it right-aligned and unquoted in the CSV. */
  value: (row: T) => string | number;
  /** Marks a numeric column, for PDF alignment. */
  numeric?: boolean;
}

export interface ExportRequest<T> {
  /** Report name — the PDF heading and the basis of the file name. */
  title: string;
  columns: ExportColumn<T>[];
  rows: T[];
  /**
   * The filters that produced `rows`, as label/value pairs.
   *
   * Printed into both formats. An exported file outlives the screen it came from, and a reader
   * who cannot tell whether a 12-row report is the whole company or one filtered team has no way
   * to interpret it.
   */
  meta?: { label: string; value: string }[];
}

const stamp = (): string => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
};

const fileBase = (title: string): string =>
  `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-${stamp()}`;

/**
 * Quote a CSV field.
 *
 * Always quoted rather than only when it contains a delimiter: employee names carry commas and
 * apostrophes, reasons carry newlines, and a leading `=` or `+` in an unquoted field is executed
 * as a formula by Excel. Quoting unconditionally removes the whole class.
 */
const csvCell = (value: string | number): string => {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

export function buildCsv<T>(request: ExportRequest<T>): string {
  const lines: string[] = [];

  if (request.meta?.length) {
    for (const m of request.meta) lines.push(`${csvCell(m.label)},${csvCell(m.value)}`);
    lines.push('');
  }

  lines.push(request.columns.map(c => csvCell(c.header)).join(','));
  for (const row of request.rows) {
    lines.push(request.columns.map(c => csvCell(c.value(row))).join(','));
  }

  return lines.join('\r\n');
}

const triggerDownload = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoked on a later tick: revoking synchronously can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/**
 * CSV, opened natively by Excel.
 *
 * The UTF-8 BOM is deliberate: without it Excel on Windows reads the file as the local codepage
 * and mangles every non-ASCII name. `.csv` rather than a real `.xlsx` because it needs no new
 * dependency and Excel treats it as a first-class spreadsheet.
 */
export function exportReportCsv<T>(request: ExportRequest<T>): void {
  const blob = new Blob(['﻿', buildCsv(request)], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, `${fileBase(request.title)}.csv`);
}

export function exportReportPdf<T>(request: ExportRequest<T>): void {
  // Landscape past six columns — the Employee Summary grows a column per active leave type, and
  // portrait silently squeezes them into unreadable slivers.
  const landscape = request.columns.length > 6;
  const doc = new jsPDF({ orientation: landscape ? 'landscape' : 'portrait' });

  doc.setFontSize(15);
  doc.text(request.title, 14, 16);

  doc.setFontSize(9);
  doc.setTextColor(110);
  let y = 22;
  doc.text(`Generated ${new Date().toLocaleString()}`, 14, y);
  for (const m of request.meta || []) {
    y += 5;
    doc.text(`${m.label}: ${m.value}`, 14, y);
  }
  y += 4;

  doc.setTextColor(0);
  autoTable(doc, {
    startY: y,
    head: [request.columns.map(c => c.header)],
    body: request.rows.map(row => request.columns.map(c => String(c.value(row)))),
    styles: { fontSize: 8, cellPadding: 2.5 },
    headStyles: { fillColor: [13, 148, 136], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [246, 248, 250] },
    columnStyles: request.columns.reduce((acc, c, i) => {
      if (c.numeric) acc[i] = { halign: 'right' };
      return acc;
    }, {} as Record<number, { halign: 'right' }>),
    // Row count in the footer: a reader can then tell a short report from a truncated one.
    didDrawPage: () => {
      const pageSize = doc.internal.pageSize;
      const height = pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(130);
      doc.text(`${request.rows.length} row(s)`, 14, height - 8);
      doc.setTextColor(0);
    },
  });

  doc.save(`${fileBase(request.title)}.pdf`);
}
